import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import JSZip from "jszip";
import { useApp } from "../store";
import { Upload, X, Check, AlertCircle, Trash2, FileText, Beaker, FileCheck2, Archive } from "lucide-react";

/** Fuzzy score — how much of `needle` appears (in order) inside `haystack`.
 *  Higher = better match. */
function fuzzyScore(needle, haystack) {
  if (!needle || !haystack) return 0;
  if (haystack.includes(needle)) return needle.length * 3;
  let score = 0, hi = 0;
  for (const ch of needle) {
    const found = haystack.indexOf(ch, hi);
    if (found !== -1) {
      score += 1;
      hi = found + 1;
    }
  }
  return score;
}

function keyify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function guessProductForFilename(filename, products) {
  const stem = keyify(filename.replace(/\.[^.]+$/, ""));
  if (!stem) return null;
  let best = null, bestScore = 0;
  for (const p of products) {
    const nameKey = keyify(p.name);
    const botanicalKey = keyify(p.botanical_name || "");
    const idKey = keyify(p.id);
    const s = Math.max(
      fuzzyScore(nameKey, stem),
      fuzzyScore(stem, nameKey),
      fuzzyScore(botanicalKey, stem),
      fuzzyScore(stem, botanicalKey),
      stem === idKey ? 100 : 0,
    );
    if (s > bestScore && s >= 3) {
      bestScore = s; best = p;
    }
  }
  return best;
}

/** Detect doc type from filename. Default to COA (safer — usually smaller docs).
 *  Keywords sourced from typical B2B naming conventions. */
function guessDocType(filename) {
  const lower = (filename || "").toLowerCase();
  const gcmsHints = ["gcms", "gc_ms", "gc-ms", "gc ms", "chromatograph", "chromato"];
  const coaHints  = ["coa", "certificate", "analysis", "cert_", "cert-", "cofa", "c_o_a"];
  if (gcmsHints.some(k => lower.includes(k))) return "gcms";
  if (coaHints.some(k => lower.includes(k))) return "coa";
  return "coa";
}

const ACCEPT_EXT = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png", ".webp", ".xls", ".xlsx"];
const ACCEPT_WITH_ZIP = [...ACCEPT_EXT, ".zip"];

function isAcceptedFile(file) {
  const name = (file.name || "").toLowerCase();
  return ACCEPT_EXT.some(ext => name.endsWith(ext));
}

function isZipFile(file) {
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".zip");
}

/** Extract every non-directory, accepted-extension file out of a JSZip archive.
 *  Preserves the base filename (drops any nested folder prefix). */
async function extractZipToFiles(zipFile) {
  const buf = await zipFile.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);
  const out = [];
  const entries = Object.values(zip.files);
  for (const entry of entries) {
    if (entry.dir) continue;
    const nameOnly = entry.name.split("/").pop();
    if (!nameOnly || nameOnly.startsWith(".") || nameOnly.startsWith("__MACOSX")) continue;
    const lower = nameOnly.toLowerCase();
    if (!ACCEPT_EXT.some(ext => lower.endsWith(ext))) continue;
    const blob = await entry.async("blob");
    // Wrap in File so downstream code (which reads .name) works identically.
    out.push(new File([blob], nameOnly, { type: blob.type || "application/octet-stream" }));
  }
  return out;
}

export default function BulkDocumentManager({ onClose }) {
  const { API, authHeaders } = useApp();
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]); // {id, file, productId, docType, label, status, message, uploadedUrl}
  const [dragOver, setDragOver] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [zipExpanding, setZipExpanding] = useState(false);
  const [zipInfo, setZipInfo] = useState(""); // e.g. "Unpacked 18 files from products.zip"

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data));
  }, [API]);

  const refreshProducts = useCallback(async () => {
    const r = await axios.get(`${API}/products`);
    setProducts(r.data);
  }, [API]);

  const addFiles = useCallback(async (files) => {
    const raw = Array.from(files || []);
    if (!raw.length) return;

    // Split into direct files vs zip archives. Unpack zips before enqueueing.
    const direct = raw.filter(isAcceptedFile);
    const zips = raw.filter(isZipFile);

    let expanded = [...direct];
    if (zips.length) {
      setZipExpanding(true);
      try {
        for (const z of zips) {
          const inner = await extractZipToFiles(z);
          expanded.push(...inner);
          setZipInfo(`Unpacked ${inner.length} file${inner.length === 1 ? "" : "s"} from ${z.name}`);
        }
      } catch (e) {
        setZipInfo(`Could not read zip: ${e.message || "unknown error"}`);
      } finally {
        setZipExpanding(false);
      }
    }

    if (!expanded.length) return;
    const next = expanded.map((file, idx) => {
      const guess = guessProductForFilename(file.name, products);
      return {
        id: `${Date.now()}-${idx}-${file.name}`,
        file,
        productId: guess?.id || "",
        docType: guessDocType(file.name),
        label: "",
        status: "pending",
        message: "",
        uploadedUrl: "",
      };
    });
    setRows(prev => [...prev, ...next]);
  }, [products]);

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const patchRow = (id, patch) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const uploadOne = async (row) => {
    if (!row.productId) {
      patchRow(row.id, { status: "error", message: "No product assigned." });
      return null;
    }
    if (!["gcms", "coa"].includes(row.docType)) {
      patchRow(row.id, { status: "error", message: "Pick GCMS or COA." });
      return null;
    }
    patchRow(row.id, { status: "uploading", message: "" });
    try {
      const fd = new FormData(); fd.append("file", row.file);
      const up = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      const publicUrl = `${process.env.REACT_APP_BACKEND_URL}${up.data.url}`;
      patchRow(row.id, { status: "uploaded", uploadedUrl: publicUrl });
      return {
        rowId: row.id,
        product_id: row.productId,
        type: row.docType,
        filename: row.file.name,
        url: publicUrl,
        label: row.label || "",
      };
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      patchRow(row.id, { status: "error", message: typeof msg === "string" ? msg : "Upload failed" });
      return null;
    }
  };

  const uploadAndAttachAll = async () => {
    if (bulkRunning) return;
    setBulkRunning(true);
    try {
      const pending = rows.filter(r => r.status === "pending" || r.status === "error");
      // Phase 1: upload all files to Emergent Object Storage in parallel (4 at a time)
      const BATCH = 4;
      const successful = [];
      for (let i = 0; i < pending.length; i += BATCH) {
        const chunk = pending.slice(i, i + BATCH);
        const results = await Promise.all(chunk.map(uploadOne));
        results.forEach(r => { if (r) successful.push(r); });
      }
      // Phase 2: attach uploaded files to their products in one bulk call
      if (successful.length) {
        try {
          const attachments = successful.map(({ rowId, ...rest }) => rest);
          await axios.post(`${API}/admin/products/bulk-documents`,
            { attachments }, { headers: authHeaders });
          successful.forEach(s => patchRow(s.rowId, { status: "saved", message: "" }));
          await refreshProducts();
        } catch (e) {
          const msg = e?.response?.data?.detail || e.message || "Attach failed";
          successful.forEach(s => patchRow(s.rowId, { status: "error", message: typeof msg === "string" ? msg : "Attach failed" }));
        }
      }
    } finally {
      setBulkRunning(false);
    }
  };

  const savedCount = rows.filter(r => r.status === "saved").length;
  const errorCount = rows.filter(r => r.status === "error").length;
  const pendingCount = rows.filter(r => r.status === "pending").length;

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const items = e.dataTransfer?.items;
    if (items) {
      const files = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      addFiles(files);
    } else {
      addFiles(e.dataTransfer?.files || []);
    }
  };

  const deleteExistingDoc = async (productId, docId) => {
    if (!window.confirm("Remove this file from the product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}/documents/${docId}`,
        { headers: authHeaders });
      await refreshProducts();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not delete");
    }
  };

  const productDocCount = (p) => (p.documents || []).length;
  const productDocsByType = (p, type) => (p.documents || []).filter(d => d.type === type);

  return (
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-start justify-center p-4 overflow-y-auto" data-testid="bulk-doc-manager">
      <div className="bg-[#FAF9F6] w-full max-w-7xl my-8 p-8">
        <div className="flex justify-between items-start mb-4 sticky top-0 bg-[#FAF9F6] pb-4 border-b border-[#E5E1D8] z-10">
          <div>
            <div className="overline text-copper mb-1">Lab Reports · Bulk Upload</div>
            <h3 className="font-serif-display text-3xl">Attach GCMS &amp; COA files to products</h3>
            <p className="text-sm text-[#5C5E5C] mt-2 max-w-3xl">
              Drop <b>individual PDFs</b> or one <b>.zip</b> containing all your reports at once.
              Filenames like <code>ruh-gulab-gcms.pdf</code> or <code>mitti-attar-coa.pdf</code>
              are auto-matched to products AND auto-tagged as GCMS or COA.
              Files are saved to persistent cloud storage. Only visitors who submit an inquiry can download them.
            </p>
          </div>
          <button onClick={onClose} data-testid="bulk-doc-close" className="p-2"><X size={22} /></button>
        </div>

        {/* Drop zone */}
        <label
          onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`block border-2 border-dashed cursor-pointer text-center py-14 mb-6 transition-colors ${
            dragOver ? "border-copper bg-[#F3EFE9]" : "border-[#E5E1D8] hover:border-copper"
          }`}
          data-testid="bulk-doc-dropzone"
        >
          <Upload size={32} className="mx-auto mb-3 text-[#8A8D8A]" strokeWidth={1.2} />
          <div className="font-serif-display text-2xl mb-1">Drop lab reports here</div>
          <div className="text-xs text-[#8A8D8A] mb-3">
            PDF, DOC, DOCX, XLS, JPG — or a <b>.zip</b> containing all of them.<br/>
            Nested folders inside the zip work — filenames are auto-matched to products.
          </div>
          {zipExpanding && (
            <div className="text-xs text-copper mb-3 flex items-center justify-center gap-2">
              <Archive size={14} className="animate-pulse" strokeWidth={1.5} />
              Unpacking zip…
            </div>
          )}
          {zipInfo && !zipExpanding && (
            <div className="text-xs text-[#3C6348] mb-3" data-testid="bulk-doc-zip-info">✓ {zipInfo}</div>
          )}
          <input
            type="file"
            accept={ACCEPT_WITH_ZIP.join(",")}
            multiple
            onChange={e => { addFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
            data-testid="bulk-doc-file-picker"
          />
          <span className="inline-block border border-copper text-copper px-6 py-2 text-xs overline">Choose files or a .zip</span>
        </label>

        {/* Queue */}
        {rows.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="overline text-copper text-xs">
                {rows.length} file{rows.length === 1 ? "" : "s"} queued
                {savedCount > 0 && <span className="text-[#3C6348] ml-3">✓ {savedCount} attached</span>}
                {errorCount > 0 && <span className="text-[#9E3C3C] ml-3">✗ {errorCount} failed</span>}
                {pendingCount > 0 && <span className="text-[#8A8D8A] ml-3">{pendingCount} pending</span>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setRows([])}
                  disabled={bulkRunning}
                  className="text-xs text-[#9E3C3C] underline hover:opacity-80 disabled:opacity-40"
                  data-testid="bulk-doc-clear"
                >
                  Clear queue
                </button>
                <button
                  onClick={uploadAndAttachAll}
                  disabled={bulkRunning || (pendingCount + errorCount) === 0}
                  className="btn-primary text-xs disabled:opacity-40"
                  data-testid="bulk-doc-upload-all"
                >
                  {bulkRunning ? "Uploading…" : `Upload & attach (${pendingCount + errorCount})`}
                </button>
              </div>
            </div>

            <div className="border border-[#E5E1D8]">
              <div className="grid grid-cols-12 gap-3 text-[10px] overline text-[#8A8D8A] px-3 py-2 border-b border-[#E5E1D8]">
                <div className="col-span-3">Filename</div>
                <div className="col-span-3">Product</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Label (optional)</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1"></div>
              </div>
              {rows.map(r => (
                <div key={r.id} className="grid grid-cols-12 gap-3 items-center px-3 py-2 border-b border-[#E5E1D8] text-sm" data-testid={`bulk-doc-row-${r.id}`}>
                  <div className="col-span-3 flex items-center gap-2 text-xs truncate" title={r.file.name}>
                    <FileText size={14} className="text-copper shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{r.file.name}</span>
                  </div>
                  <div className="col-span-3">
                    <select
                      value={r.productId}
                      onChange={e => patchRow(r.id, { productId: e.target.value })}
                      disabled={r.status === "uploading" || r.status === "saved"}
                      className="input-underline w-full text-xs"
                      data-testid={`bulk-doc-product-${r.id}`}
                    >
                      <option value="">— Select product —</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({productDocCount(p)} on file)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => patchRow(r.id, { docType: "coa" })}
                        disabled={r.status === "uploading" || r.status === "saved"}
                        className={`flex-1 py-1 text-[10px] uppercase tracking-wider border ${r.docType === "coa" ? "border-[#1E1F1E] bg-[#1E1F1E] text-[#FAF9F6]" : "border-[#E5E1D8] hover:border-[#1E1F1E]"}`}
                        data-testid={`bulk-doc-type-coa-${r.id}`}
                      >
                        COA
                      </button>
                      <button
                        onClick={() => patchRow(r.id, { docType: "gcms" })}
                        disabled={r.status === "uploading" || r.status === "saved"}
                        className={`flex-1 py-1 text-[10px] uppercase tracking-wider border ${r.docType === "gcms" ? "border-[#1E1F1E] bg-[#1E1F1E] text-[#FAF9F6]" : "border-[#E5E1D8] hover:border-[#1E1F1E]"}`}
                        data-testid={`bulk-doc-type-gcms-${r.id}`}
                      >
                        GCMS
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <input
                      value={r.label}
                      onChange={e => patchRow(r.id, { label: e.target.value })}
                      placeholder="e.g. Lot #2024-A"
                      disabled={r.status === "uploading" || r.status === "saved"}
                      className="input-underline w-full text-xs"
                      data-testid={`bulk-doc-label-${r.id}`}
                    />
                  </div>
                  <div className="col-span-1 text-xs">
                    {r.status === "pending" && <span className="text-[#8A8D8A]">Pending</span>}
                    {r.status === "uploading" && <span className="text-copper">Up…</span>}
                    {r.status === "uploaded" && <span className="text-copper">Linking…</span>}
                    {r.status === "saved" && <span className="text-[#3C6348] flex items-center gap-1"><Check size={12} /> Saved</span>}
                    {r.status === "error" && (
                      <span className="text-[#9E3C3C] flex items-center gap-1" title={r.message}>
                        <AlertCircle size={12} /> Err
                      </span>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => removeRow(r.id)}
                      disabled={r.status === "uploading"}
                      className="text-[#9E3C3C] hover:opacity-80 disabled:opacity-30"
                      data-testid={`bulk-doc-remove-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {errorCount > 0 && (
              <div className="mt-2 text-[10px] text-[#9E3C3C]">
                Hover the ✗ Err badge or expand a row to see the exact error message. Common causes: no product assigned, or file &gt; 50 MB.
              </div>
            )}
          </div>
        )}

        {/* Existing documents per product */}
        <div>
          <div className="overline text-xs text-[#8A8D8A] mb-3">Current lab reports on file</div>
          <div className="grid md:grid-cols-2 gap-1">
            {products.map(p => {
              const coa = productDocsByType(p, "coa");
              const gcms = productDocsByType(p, "gcms");
              const total = coa.length + gcms.length;
              return (
                <div key={p.id} className="bg-sand p-4" data-testid={`bulk-doc-summary-${p.id}`}>
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8A8D8A]">
                      {total} on file
                    </div>
                  </div>
                  {total === 0 ? (
                    <div className="text-xs text-[#8A8D8A] italic">No reports yet — drop files above to attach.</div>
                  ) : (
                    <div className="space-y-1">
                      {coa.map(d => (
                        <DocRow key={d.id} doc={d} icon={<FileCheck2 size={12} strokeWidth={1.5} />} typeLabel="COA"
                          onDelete={() => deleteExistingDoc(p.id, d.id)} testId={`existing-doc-${d.id}`} />
                      ))}
                      {gcms.map(d => (
                        <DocRow key={d.id} doc={d} icon={<Beaker size={12} strokeWidth={1.5} />} typeLabel="GCMS"
                          onDelete={() => deleteExistingDoc(p.id, d.id)} testId={`existing-doc-${d.id}`} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FAF9F6] border-t border-[#E5E1D8] mt-8 pt-4 flex items-center justify-between">
          <span className="text-sm text-[#5C5E5C]" data-testid="bulk-doc-progress">
            {savedCount > 0 ? `✓ ${savedCount} report${savedCount === 1 ? "" : "s"} attached` : rows.length ? `${rows.length} queued` : "Ready when you are."}
          </span>
          <button onClick={onClose} className="btn-primary" data-testid="bulk-doc-done">Done</button>
        </div>
      </div>
    </div>
  );
}

function DocRow({ doc, icon, typeLabel, onDelete, testId }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs bg-[#FAF9F6]/60 px-2 py-1.5" data-testid={testId}>
      <a href={doc.url} target="_blank" rel="noreferrer"
         className="flex items-center gap-1.5 text-copper hover:underline truncate flex-1"
         title={doc.filename}>
        {icon}
        <span className="uppercase tracking-wider text-[9px] shrink-0">{typeLabel}</span>
        <span className="truncate">{doc.label || doc.filename}</span>
      </a>
      <button onClick={onDelete} className="text-[#9E3C3C] hover:opacity-70 shrink-0" title="Remove">
        <Trash2 size={11} />
      </button>
    </div>
  );
}
